/**
 * Валидация полей регистрации и профиля (клиент).
 * Лимиты должны совпадать с server/validation/validation.js
 */

import { isAllowedJobTitle } from '../config/jobTitles.ts';

/** Ключ перевода для ошибки «нежелательные слова в нике» */
export const USERNAME_FORBIDDEN_WORDS_ERROR_KEY = 'registration.usernameForbiddenWords';

/** Ключ перевода для ошибки «недопустимый домен email при регистрации» */
export const EMAIL_DOMAIN_NOT_ALLOWED_KEY = 'registration.emailDomainNotAllowed';

/** Разрешённые домены email при регистрации (должны совпадать с server/validation/validation.js) */
export const ALLOWED_REGISTRATION_EMAIL_DOMAINS = ['yandex.ru', 'mail.ru'] as const;

/** Нежелательные слова в нике (проверка без учёта регистра) */
const FORBIDDEN_USERNAME_WORDS = [
  'admin', 'administrator', 'moderator', 'support', 'root', 'system',
  'fuck', 'shit', 'ass', 'bitch', 'damn', 'crap', 'wtf', 'stfu',
  'идиот', 'дурак', 'сука', 'блять', 'пизда', 'хуй', 'нахер', 'мудак',
];

function containsForbiddenWord(username: string): boolean {
  const lower = (username || '').toLowerCase();
  return FORBIDDEN_USERNAME_WORDS.some((word) => {
    const w = word.toLowerCase();
    return lower.includes(w) || lower === w;
  });
}

export const LIMITS = {
  USERNAME_MIN: 3,
  USERNAME_MAX: 30,
  USERNAME_PATTERN: /^[a-zA-Z0-9_]+$/,
  EMAIL_MAX: 255,
  EMAIL_PATTERN: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PASSWORD_MIN: 8,
  PASSWORD_MAX: 128,
  FIRST_NAME_MAX: 50,
  LAST_NAME_MAX: 50,
  PHONE_MAX: 20,
  PHONE_PATTERN: /^[0-9+\s\-().]*$/,
  JOB_TITLE_MAX: 2,
  UNIVERSITY_MAX: 200,
  SKILLS_MAX: 20,
  SKILL_ITEM_MAX: 100,
  BIRTHDAY_PATTERN: /^\d{4}-\d{2}-\d{2}$/,
};

export function validateUsername(v: string): string | null {
  const s = (v || '').trim();
  if (s.length === 0) return 'Username обязателен';
  if (s.length < LIMITS.USERNAME_MIN || s.length > LIMITS.USERNAME_MAX)
    return `Username: от ${LIMITS.USERNAME_MIN} до ${LIMITS.USERNAME_MAX} символов`;
  if (!LIMITS.USERNAME_PATTERN.test(s)) return 'Username: только латиница, цифры и _';
  if (containsForbiddenWord(s)) return USERNAME_FORBIDDEN_WORDS_ERROR_KEY;
  return null;
}

export function getEmailDomain(v: string): string {
  const s = (v || '').trim().toLowerCase();
  const at = s.lastIndexOf('@');
  return at >= 0 ? s.slice(at + 1) : '';
}

export function isAllowedRegistrationEmailDomain(v: string): boolean {
  const domain = getEmailDomain(v);
  return (ALLOWED_REGISTRATION_EMAIL_DOMAINS as readonly string[]).includes(domain);
}

export function validateEmail(v: string): string | null {
  const s = (v || '').trim();
  if (s.length === 0) return 'Email обязателен';
  if (s.length > LIMITS.EMAIL_MAX) return `Email: макс. ${LIMITS.EMAIL_MAX} символов`;
  if (!LIMITS.EMAIL_PATTERN.test(s)) return 'Некорректный формат email';
  return null;
}

export function validateRegistrationEmailDomain(v: string): string | null {
  const formatErr = validateEmail(v);
  if (formatErr) return formatErr;
  if (!isAllowedRegistrationEmailDomain(v)) return EMAIL_DOMAIN_NOT_ALLOWED_KEY;
  return null;
}

export function validatePassword(v: string): string | null {
  if (v == null || typeof v !== 'string') return 'Пароль обязателен';
  if (v.length < LIMITS.PASSWORD_MIN || v.length > LIMITS.PASSWORD_MAX)
    return `Пароль: от ${LIMITS.PASSWORD_MIN} до ${LIMITS.PASSWORD_MAX} символов`;
  return null;
}

export function validateFirstName(v: string): string | null {
  if (v == null || v === '') return null;
  if (typeof v !== 'string') return 'Имя: неверный формат';
  if (v.length > LIMITS.FIRST_NAME_MAX) return `Имя: макс. ${LIMITS.FIRST_NAME_MAX} символов`;
  return null;
}

export function validateLastName(v: string): string | null {
  if (v == null || v === '') return null;
  if (typeof v !== 'string') return 'Фамилия: неверный формат';
  if (v.length > LIMITS.LAST_NAME_MAX) return `Фамилия: макс. ${LIMITS.LAST_NAME_MAX} символов`;
  return null;
}

export function validatePhone(v: string): string | null {
  if (v == null || v === '') return null;
  const s = String(v);
  if (s.length > LIMITS.PHONE_MAX) return `Телефон: макс. ${LIMITS.PHONE_MAX} символов`;
  if (!LIMITS.PHONE_PATTERN.test(s)) return 'Телефон: только цифры, +, пробелы, - ( ) .';
  return null;
}

export function validateJobTitle(arr: unknown): string | null {
  if (arr == null) return null;
  if (!Array.isArray(arr)) return 'Должность: неверный формат';
  if (arr.length > LIMITS.JOB_TITLE_MAX) return `Должность: макс. ${LIMITS.JOB_TITLE_MAX}`;
  for (let i = 0; i < arr.length; i++) {
    if (typeof arr[i] !== 'string') return 'Должность: каждый элемент — строка';
    const v = (arr[i] as string).trim();
    if (!v) continue;
    if (!isAllowedJobTitle(v)) return 'Должность: выберите значение из списка профессий';
  }
  return null;
}

export function validateSkills(skills: unknown): string | null {
  if (skills == null) return null;
  if (!Array.isArray(skills)) return 'Навыки: неверный формат';
  if (skills.length > LIMITS.SKILLS_MAX) return `Навыки: макс. ${LIMITS.SKILLS_MAX}`;
  for (let i = 0; i < skills.length; i++) {
    if (typeof skills[i] !== 'string') return 'Навыки: каждый элемент — строка';
    if ((skills[i] as string).length > LIMITS.SKILL_ITEM_MAX) return `Навыки: макс. ${LIMITS.SKILL_ITEM_MAX} симв. на элемент`;
  }
  return null;
}

export function validateBirthday(v: string | null): string | null {
  if (v == null || v === '') return null;
  const b = String(v).slice(0, 10);
  if (!LIMITS.BIRTHDAY_PATTERN.test(b)) return 'Дата рождения: формат ГГГГ-ММ-ДД';
  const d = new Date(b);
  if (isNaN(d.getTime())) return 'Дата рождения: некорректная дата';
  if (d > new Date()) return 'Дата рождения: не может быть в будущем';
  return null;
}

export function validateRegistration(data: { username: string; email: string; password: string }): string | null {
  return validateUsername(data.username) ?? validateRegistrationEmailDomain(data.email) ?? validatePassword(data.password);
}

export function validateUniversity(v: unknown): string | null {
  if (v == null || v === '') return null;
  if (typeof v !== 'string') return 'Вуз: неверный формат';
  if (v.length > LIMITS.UNIVERSITY_MAX) return `Вуз: макс. ${LIMITS.UNIVERSITY_MAX} символов`;
  return null;
}

export function validateProfileForm(data: {
  username?: string;
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
  job_title?: unknown;
  university?: string | null;
  skills?: unknown;
  birthday?: string | null;
}): string | null {
  if (data.username != null) {
    const e = validateUsername(data.username);
    if (e) return e;
  }
  if (data.firstName != null && data.firstName !== '') {
    const e = validateFirstName(data.firstName);
    if (e) return e;
  }
  if (data.lastName != null && data.lastName !== '') {
    const e = validateLastName(data.lastName);
    if (e) return e;
  }
  if (data.phone != null && data.phone !== '') {
    const e = validatePhone(data.phone);
    if (e) return e;
  }
  if (data.job_title != null) {
    const e = validateJobTitle(data.job_title);
    if (e) return e;
  }
  if (data.university != null) {
    const e = validateUniversity(data.university);
    if (e) return e;
  }
  if (data.skills != null) {
    const e = validateSkills(data.skills);
    if (e) return e;
  }
  if (data.birthday != null && data.birthday !== '') {
    const e = validateBirthday(data.birthday);
    if (e) return e;
  }
  return null;
}
